import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import ProjectStatusChip from '~components/Project/ProjectStatusChip';
import { Project } from '~hooks/useProjects/models';
import useDeletedRecordsCursor from '~pages/Settings/DeletedRecords/useDeletedRecordsCursor';
import useDiscardedProjectsQuery from '~queries/useDiscardedProjectsQuery';

interface DeletedProjectsTabProps {
  companyId: string;
}

export default function DeletedProjectsTab({ companyId }: DeletedProjectsTabProps) {
  const { cursor, goToPage, paginationForDisplay } = useDeletedRecordsCursor(companyId);

  const { isFetching, pagination, projects } = useDiscardedProjectsQuery({
    companyId,
    cursor: cursor.cursor,
    direction: cursor.direction,
  });

  const columns = useMemo<Array<DataTableColumn<Project>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (project) => project.name,
      },
      {
        field: 'projectId',
        headerName: t('settings.deleted_records.columns.project_number'),
        valueGetter: (project) => project.projectId,
      },
      {
        field: 'customerAccount',
        headerName: t('common.customer'),
        valueGetter: (project) => project.account?.name,
      },
      {
        field: 'state',
        headerName: t('form_fields.status'),
        renderCell: (project) => <ProjectStatusChip project={project} />,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.external_id'),
        valueGetter: (project) => project.externalId,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <DataTable
        id="deleted-records-projects-grid"
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            pagination={paginationForDisplay(pagination)}
            onNavigate={(direction) => goToPage(direction, pagination)}
          />
        }
        loading={isFetching}
        noRowsOverlayText={t('settings.deleted_records.no_deleted_projects')}
        rows={projects}
      />
    </Box>
  );
}
